import { type FC, useCallback, useEffect, useMemo } from 'react';

import { SaveSearchContainer, type TOnSubmitSaveSearchParams, useSaveSearchEvents } from '@/packages/SaveSearchModal';
import { useSearchOffersDesktopQuery } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/query';
import { useUserSelector } from '@/packages/redux-state/store/slices/user';

import { trackOpen } from './tracking';
import { usePushManager } from './usePushManager';

type TSaveSearchModalContainerProps = {
  isOpen: boolean;
  onSubscribed: (parameters: TOnSubmitSaveSearchParams) => void;
  onPushSubscribed: (subscribeToPushAvailable: boolean) => void;
  onOpen: () => void;
  onClose: () => void;
};

export const SaveSearchModalContainer: FC<TSaveSearchModalContainerProps> = ({
  isOpen,
  onSubscribed,
  onPushSubscribed,
  onOpen,
  onClose,
}) => {
  const { data } = useSearchOffersDesktopQuery();
  const defaultTitle = data?.pages[0].seo.view.title ?? null;
  const user = useUserSelector();
  const defaultEmail = useMemo(() => (user?.isAuthenticated && user.email ? user.email : null), [user]);
  const { subscribeToPushAvailable, handlePushSubscribe } = usePushManager(isOpen, trackOpen);

  useEffect(() => {
    onPushSubscribed(subscribeToPushAvailable);
  }, [subscribeToPushAvailable]);

  const handleSubscribed = useCallback(
    async ({ frequency, title, email, subscribeToNews, subscribeToPush }: TOnSubmitSaveSearchParams) => {
      if (onSubscribed) {
        onSubscribed({
          frequency,
          title,
          email,
          subscribeToNews,
          defaultTitle,
          defaultEmail,
          subscribeToPushAvailable,
          subscribeToPush,
        });
      }

      await handlePushSubscribe();

      if (window.__reloadHeader__) {
        window.__reloadHeader__();
      }
    },
    [defaultEmail, defaultTitle, subscribeToPushAvailable, onSubscribed, handlePushSubscribe],
  );

  useSaveSearchEvents(handleSubscribed, onOpen, onClose);

  return <SaveSearchContainer />;
};
